import { IParamFilterShipment, IShipmentInfo } from "../../models/Tracking";
import {
  Form,
  Modal,
  Table,
  TableColumnType,
  Row,
  Input,
  Col,
  DatePicker,
  InputNumber,
  Select,
  Button,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useTransition } from "react";
import { useUpdateShipmentInfo } from "../../query/Tracking";
import { notify } from "../../utils/toast";

interface IModalEditShipmentInfoProps {
  isOpen: boolean;
  onClose: () => void;
  params: IParamFilterShipment;
  shipment: IShipmentInfo;
}

const ModalEditShipmentInfo = ({
  isOpen,
  onClose,
  params,
  shipment,
}: IModalEditShipmentInfoProps) => {
  const [form] = Form.useForm();
  const [isPending, startTransition] = useTransition();
  const updateShipment = useUpdateShipmentInfo();
  useEffect(() => {
    if (isOpen && shipment) {
      form.setFieldsValue({
        openDate: shipment.openDate ? dayjs(shipment.openDate) : null,
        declarationNumber: shipment.declarationNumber,
        branchNumber: shipment.branchNumber,
        declarationEndDate: shipment.declarationEndDate
          ? dayjs(shipment.declarationEndDate)
          : null,
        declarationStatus: shipment.declarationStatus ? true : false,
        declarationProcessingDays: shipment.declarationProcessingDays,
        daysDelayedScheduled: shipment.daysDelayedScheduled,
        releaseDate: shipment.releaseDate ? dayjs(shipment.releaseDate) : null,
        warehouseArrivalDate: shipment.warehouseArrivalDate
          ? dayjs(shipment.warehouseArrivalDate)
          : null,
        deliveryStatus: shipment.deliveryStatus ? true : false,
        notes: shipment.notes,
      });
    } else {
      form.resetFields();
    }
  }, [isOpen, shipment, form]);

  const handleSubmit = async () => {
    startTransition(async () => {
      try {
        const values = await form.validateFields();
        const changedValues: Record<string, any> = {};

        Object.keys(values).forEach((key) => {
          const newValue = values[key];
          const oldValue = (shipment as any)[key];

          if (
            newValue !== oldValue &&
            newValue !== null &&
            newValue !== undefined
          ) {
            if (dayjs.isDayjs(newValue)) {
              changedValues[key] = newValue.format("YYYY-MM-DD");
            } else {
              changedValues[key] = newValue;
            }
          }
        });
        await updateShipment.mutateAsync({
          id: shipment.id,
          ...changedValues,
        });
        notify.success("Cập nhật thông tin vận đơn thành công");
        await onClose();
      } catch (error) {
        notify.error("Cập nhật thông tin vận đơn thất bại");
      }
    });
  };
  const infoData = [
    { key: "hawbHbl", label: "Mã vận đơn", value: shipment.hawbHbl },
    {
      key: "carrierFowarder",
      label: "Nhà cung cấp",
      value: shipment.carrierForwarder,
    },
    {
      key: "shipmentType",
      label: "Phương thức vận chuyển",
      value: shipment.shipmentType,
    },
    { key: "seller", label: "Người bán", value: shipment.seller },
    { key: "ata", label: "ATA", value: shipment.ata },
    { key: "cdsPic", label: "CDS PIC", value: shipment.cdsPic },
  ];

  const columns: TableColumnType<(typeof infoData)[0]>[] = [
    {
      title: "Trường",
      dataIndex: "label",
      key: "label",
      width: 200,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Giá trị",
      dataIndex: "value",
      key: "value",
      render: (text) => text || "-",
    },
  ];

  return (
    <Modal
      title="Chỉnh sửa thông tin vận đơn"
      open={isOpen}
      onCancel={onClose}
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={isPending}>
            Hủy
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={isPending}>
            Lưu
          </Button>
        </Space>
      }
      width={1200}
    >
      <div>
        <Table
          columns={columns}
          dataSource={infoData}
          rowKey="key"
          pagination={false}
          bordered
          size="small"
        />
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item label="Ngày mở TK" name="openDate">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Số tờ khai" name="declarationNumber">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Nhập số TK"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Số nhánh" name="branchNumber">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Nhập số nhánh"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Phân luồng" name="classification">
                <Select style={{ width: "100%" }} placeholder="Chọn phân luồng">
                  <Select.Option value="GREEN">Xanh</Select.Option>
                  <Select.Option value="YELLOW">Vàng</Select.Option>
                  <Select.Option value="RED">Đỏ</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Ngày kết thúc xử lí tờ khai"
                name="declarationEndDate"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Trạng thái tờ khai" name="declarationStatus">
                <Select style={{ width: "100%" }} placeholder="Chọn trạng thái">
                  <Select.Option value={true}>Đã xong</Select.Option>
                  <Select.Option value={false}>Chưa xong</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Số ngày xử lí lô hàng"
                name="shipmentProcessingDays"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Nhập số ngày"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ngày trượt tiến độ" name="daysDelayedScheduled">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Nhập số ngày"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ngày báo phát" name="releaseDate">
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ngày về kho" name="warehouseArrivalDate">
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Trạng thái vận chuyển" name="deliveryStatus">
                <Select style={{ width: "100%" }} placeholder="Chọn trạng thái">
                  <Select.Option value={true}>Đã về kho</Select.Option>
                  <Select.Option value={false}>Chưa về kho</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ghi chú" name="notes">
                <Input.TextArea
                  rows={1}
                  style={{ width: "100%" }}
                  placeholder="Nhập ghi chú"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default ModalEditShipmentInfo;
